import { state } from '../state';
import { API_BASE, get, post } from '../api';
import { showToast, errToast } from '../components/toast';
import { confirmModal, promptModal } from '../components/modal';
import { iconSun, iconMoon, iconSearch } from '../icons';
import {
  getPermissionStatus,
  requestNotificationPermissionSync,
  isNotificationsMasterOn,
  setNotificationsMasterOn,
  getMutedChannels,
  unmuteChannel,
} from '../components/notifications';

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

type SettingsSection = 'account' | 'appearance' | 'notifications' | 'privacy' | 'contacts' | 'server' | 'roles' | 'channels' | 'management' | 'storage' | 'support' | 'audit' | 'dev-workers' | 'about';

let activeSection: SettingsSection = 'account';

export function renderSettings(body: HTMLElement): void {
  body.innerHTML = `
    <div class="settings-layout">
      <div class="settings-nav">
        <div class="settings-nav-search">
          <span class="settings-nav-search-icon" aria-hidden="true">${iconSearch()}</span>
          <input type="text" id="settings-nav-search-input" placeholder="Search settings" autocomplete="off" spellcheck="false" aria-label="Search settings">
        </div>
        <div class="settings-nav-title">User Settings</div>
        <button class="settings-nav-item ${activeSection === 'account' ? 'active' : ''}" data-sec="account">My Account</button>
        <button class="settings-nav-item ${activeSection === 'appearance' ? 'active' : ''}" data-sec="appearance">Appearance</button>
        <button class="settings-nav-item ${activeSection === 'notifications' ? 'active' : ''}" data-sec="notifications">Notifications</button>
        <button class="settings-nav-item ${activeSection === 'privacy' ? 'active' : ''}" data-sec="privacy">Privacy & Safety</button>
        <button class="settings-nav-item ${activeSection === 'contacts' ? 'active' : ''}" data-sec="contacts">Contacts</button>
        <div class="settings-nav-divider"></div>
        <div class="settings-nav-title">Server</div>
        <button class="settings-nav-item ${activeSection === 'server' ? 'active' : ''}" data-sec="server">Server Settings</button>
        <button class="settings-nav-item ${activeSection === 'roles' ? 'active' : ''}" data-sec="roles">Roles</button>
        <button class="settings-nav-item ${activeSection === 'channels' ? 'active' : ''}" data-sec="channels">Channels &amp; Permissions</button>
        <button class="settings-nav-item ${activeSection === 'management' ? 'active' : ''}" data-sec="management">Management</button>
        <button class="settings-nav-item ${activeSection === 'storage' ? 'active' : ''}" data-sec="storage">Storage</button>
        <button class="settings-nav-item ${activeSection === 'support' ? 'active' : ''}" data-sec="support">Adhoc Support</button>
        <button class="settings-nav-item ${activeSection === 'audit' ? 'active' : ''}" data-sec="audit">Audit Log</button>
        <button class="settings-nav-item ${activeSection === 'dev-workers' ? 'active' : ''}" data-sec="dev-workers">Dev Workers</button>
        <div class="settings-nav-divider"></div>
        <button class="settings-nav-item ${activeSection === 'about' ? 'active' : ''}" data-sec="about">About</button>
        <button class="settings-nav-item settings-nav-logout" id="settings-logout">Log Out</button>
        <div class="settings-nav-nomatch settings-nav-hidden" id="settings-nav-nomatch">No settings match.</div>
      </div>
      <div class="settings-content" id="settings-content"></div>
    </div>
  `;

  // Nav clicks
  body.querySelectorAll<HTMLButtonElement>('.settings-nav-item[data-sec]').forEach(btn => {
    btn.addEventListener('click', () => {
      activeSection = (btn.dataset['sec'] || 'account') as SettingsSection;
      renderSettings(body);
    });
  });

  // Nav search / filter. Filters the live nav in place (no re-render, so the
  // input keeps focus + query). Hides non-matching items, group titles whose
  // items are all hidden, and every divider while a query is active.
  wireSettingsNavSearch(body);

  // Logout
  document.getElementById('settings-logout')?.addEventListener('click', () => {
    confirmModal('Log out', 'Log out?', () => {
      document.cookie = 'hub_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      localStorage.clear();
      window.location.reload();
    }, { confirmLabel: 'Log Out', variant: 'danger' });
  });

  const content = document.getElementById('settings-content');
  if (!content) return;

  switch (activeSection) {
    case 'account': renderAccountSection(content); break;
    case 'appearance': renderAppearanceSection(content); break;
    case 'notifications': renderNotificationsSection(content); break;
    case 'privacy': renderPrivacySection(content); break;
    case 'contacts': void renderContactsSection(content); break;
    case 'server': renderServerSection(content); break;
    case 'roles': renderRolesSection(content); break;
    case 'channels': renderChannelsSection(content); break;
    case 'management': renderManagementSection(content); break;
    case 'storage': void renderStorageSection(content); break;
    case 'support': void renderSupportSection(content); break;
    case 'audit': renderAuditSection(content); break;
    case 'dev-workers': void renderDevWorkersSection(content); break;
    case 'about': renderAboutSection(content); break;
  }
}

// Wire the search box that filters the settings nav. Operates on the live DOM
// (nav clicks re-render the whole view, so this must NOT re-render — it would
// drop focus and the typed query). Uses a `.settings-nav-hidden` class rather
// than inline display so the mobile media-query rules (titles/dividers hidden)
// keep working when the class is removed.
function wireSettingsNavSearch(body: HTMLElement): void {
  const input = body.querySelector<HTMLInputElement>('#settings-nav-search-input');
  const nav = body.querySelector<HTMLElement>('.settings-nav');
  const noMatch = body.querySelector<HTMLElement>('#settings-nav-nomatch');
  if (!input || !nav) return;

  const items = Array.from(nav.querySelectorAll<HTMLButtonElement>('.settings-nav-item[data-sec]'));
  const titles = Array.from(nav.querySelectorAll<HTMLElement>('.settings-nav-title'));
  const dividers = Array.from(nav.querySelectorAll<HTMLElement>('.settings-nav-divider'));

  // Items that belong to a title = following siblings up to the next title.
  const groupItems = (title: HTMLElement): HTMLButtonElement[] => {
    const group: HTMLButtonElement[] = [];
    let sib = title.nextElementSibling;
    while (sib && !sib.classList.contains('settings-nav-title')) {
      if (sib.matches('.settings-nav-item[data-sec]')) group.push(sib as HTMLButtonElement);
      sib = sib.nextElementSibling;
    }
    return group;
  };

  const applyFilter = (raw: string): void => {
    const q = raw.trim().toLowerCase();
    let visibleCount = 0;
    for (const item of items) {
      const match = q === '' || (item.textContent || '').toLowerCase().includes(q);
      item.classList.toggle('settings-nav-hidden', !match);
      if (match) visibleCount++;
    }
    // Hide a title if all its items are hidden; hide dividers while searching.
    for (const title of titles) {
      const anyVisible = groupItems(title).some(i => !i.classList.contains('settings-nav-hidden'));
      title.classList.toggle('settings-nav-hidden', !anyVisible);
    }
    for (const divider of dividers) {
      divider.classList.toggle('settings-nav-hidden', q !== '');
    }
    if (noMatch) noMatch.classList.toggle('settings-nav-hidden', visibleCount !== 0);
  };

  input.addEventListener('input', () => applyFilter(input.value));

  // Enter activates the section when exactly one item matches.
  input.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const visible = items.filter(i => !i.classList.contains('settings-nav-hidden'));
    if (visible.length === 1) {
      e.preventDefault();
      visible[0]?.click();
    }
  });

  // Autofocus on desktop only — focusing on mobile would pop the keyboard.
  if (!window.matchMedia('(max-width: 640px)').matches) {
    input.focus();
  }
}

function renderAccountSection(el: HTMLElement): void {
  const me = state.people.find(p => p.id === state.ownerId);
  const name = me?.name || 'Unknown';
  const email = me?.email || '';
  const phone = me?.phone || '';
  const status = localStorage.getItem('hub-people-status') || 'online';

  el.innerHTML = `
    <h2 class="settings-heading">My Account</h2>
    <div class="settings-card">
      <div class="settings-profile-banner"></div>
      <div class="settings-profile-info">
        <div class="settings-avatar" id="s-avatar-preview" title="Click to change avatar" style="cursor:pointer;position:relative">
          ${esc(name.charAt(0).toUpperCase())}
          <div style="position:absolute;bottom:-2px;right:-2px;width:20px;height:20px;background:var(--accent);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;border:2px solid var(--bg-secondary)">+</div>
        </div>
        <input type="file" id="s-avatar-upload" accept="image/*" style="display:none">
        <div class="settings-profile-details">
          <div class="settings-username">${esc(name)}</div>
          <div class="settings-status-badge settings-status-${esc(status)}">${esc(status)}</div>
        </div>
      </div>
      <div class="settings-profile-fields">
        <div class="settings-field">
          <label>Display Name</label>
          <div class="settings-field-row">
            <input type="text" id="s-name" class="settings-input" value="${esc(name)}" placeholder="Your name">
          </div>
        </div>
        <div class="settings-field">
          <label>Email</label>
          <div class="settings-field-row">
            <input type="email" id="s-email" class="settings-input" value="${esc(email)}" placeholder="Email">
          </div>
        </div>
        <div class="settings-field">
          <label>Phone</label>
          <div class="settings-field-row">
            <input type="tel" id="s-phone" class="settings-input" value="${esc(phone)}" placeholder="Phone">
          </div>
        </div>
        <button class="btn btn-primary" id="s-save-profile" style="margin-top:12px">Save Changes</button>
      </div>
    </div>
  `;

  // Avatar upload
  document.getElementById('s-avatar-preview')?.addEventListener('click', () => {
    document.getElementById('s-avatar-upload')?.click();
  });
  document.getElementById('s-avatar-upload')?.addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('files', file);
    try {
      const res = await fetch(`${API_BASE}/api/uploads`, { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (data.status === 'ok' && data.data?.[0]?.url) {
        const url = data.data[0].url;
        const preview = document.getElementById('s-avatar-preview');
        if (preview) preview.innerHTML = `<img src="${url}" style="width:100%;height:100%;object-fit:cover;border-radius:50%">`;
        // Store avatar URL in localStorage for now (TODO: persist to user profile API)
        localStorage.setItem('hub-people-avatar', url);
        showToast('Avatar updated', 'ok');
      }
    } catch (err) { errToast(err); }
  });

  document.getElementById('s-save-profile')?.addEventListener('click', () => {
    const newName = (document.getElementById('s-name') as HTMLInputElement)?.value.trim();
    const newEmail = (document.getElementById('s-email') as HTMLInputElement)?.value.trim();
    const newPhone = (document.getElementById('s-phone') as HTMLInputElement)?.value.trim();
    if (!me) return;
    void post('/api/people/update', {
      id: me.id,
      name: newName || me.name,
      email: newEmail,
      phone: newPhone,
    }).then(() => {
      showToast('Profile updated', 'ok');
      if (me) {
        me.name = newName || me.name;
        me.email = newEmail;
        me.phone = newPhone;
      }
    }).catch(e => showToast(String(e), 'err'));
  });
}

function renderAppearanceSection(el: HTMLElement): void {
  const themeIcon = state.theme === 'dark' ? iconSun() : iconMoon();
  const themeLabel = state.theme === 'dark' ? 'Light Mode' : 'Dark Mode';
  const fontSize = localStorage.getItem('hub-people-fontsize') || '14';
  const compact = localStorage.getItem('hub-people-compact') === 'true';

  el.innerHTML = `
    <h2 class="settings-heading">Appearance</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Theme</label>
        <button class="btn btn-secondary" id="toggle-theme">${themeIcon} ${esc(themeLabel)}</button>
      </div>
      <div class="settings-field">
        <label>Chat Font Size</label>
        <div class="settings-field-row">
          <input type="range" id="s-fontsize" min="12" max="20" value="${esc(fontSize)}" style="flex:1">
          <span id="s-fontsize-val">${esc(fontSize)}px</span>
        </div>
      </div>
      <div class="settings-field">
        <label>Compact Mode</label>
        <div class="settings-field-row">
          <label class="settings-toggle">
            <input type="checkbox" id="s-compact" ${compact ? 'checked' : ''}>
            <span class="settings-toggle-slider"></span>
          </label>
          <span style="font-size:13px;color:var(--fg-muted)">Reduce spacing between messages</span>
        </div>
      </div>
    </div>
  `;

  document.getElementById('toggle-theme')?.addEventListener('click', () => {
    state.theme = state.theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset['theme'] = state.theme;
    localStorage.setItem('hub-people-theme', state.theme);
    renderAppearanceSection(el);
  });

  document.getElementById('s-fontsize')?.addEventListener('input', (e) => {
    const val = (e.target as HTMLInputElement).value;
    localStorage.setItem('hub-people-fontsize', val);
    document.documentElement.style.setProperty('--chat-font-size', `${val}px`);
    const label = document.getElementById('s-fontsize-val');
    if (label) label.textContent = `${val}px`;
  });

  document.getElementById('s-compact')?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    localStorage.setItem('hub-people-compact', String(checked));
    document.body.classList.toggle('compact-mode', checked);
  });
}

function renderNotificationsSection(el: HTMLElement): void {
  const masterOn = isNotificationsMasterOn();
  const soundEnabled = localStorage.getItem('hub-people-sound') !== 'false';
  const perm = getPermissionStatus();
  const muted = getMutedChannels();

  // Global "notify me for everything" state: master on + no server limited to
  // mentions/none + nothing muted → every message (from anyone but me, incl. AI
  // / automated) notifies. Categories are channel groups and role-mentions ride
  // the all-messages path, so this one control covers all channels/cats/roles.
  const myId = state.ownerId || state.people[0]?.id || '';
  const myServerIds = state.serverMembers.filter((m) => m.person_id === myId).map((m) => m.server_id);
  const myServers = state.servers.filter((s) => myServerIds.includes(s.id));
  let serverModes: Record<string, string> = {};
  try { serverModes = JSON.parse(localStorage.getItem('hub-people-server-notifs') || '{}'); } catch { /* ignore */ }
  const allOn = masterOn
    && myServers.every((s) => serverModes[s.id] !== 'mentions' && serverModes[s.id] !== 'none')
    && muted.length === 0;

  const permLabel = (
    perm === 'granted' ? 'Granted' :
    perm === 'denied' ? 'Denied' :
    perm === 'default' ? 'Not set' :
    'Unsupported'
  );
  const showRequestBtn = perm === 'default' || perm === 'denied';

  el.innerHTML = `
    <h2 class="settings-heading">Notifications</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Notify me for every message</label>
        <div class="settings-field-row" style="gap:10px;flex-wrap:wrap">
          <span style="font-size:13px;color:${allOn ? 'var(--ok,#16a34a)' : 'var(--fg-muted)'};flex:1;min-width:180px">${allOn ? 'ON — every channel, category &amp; role, from anyone but you (incl. AI &amp; automated posts).' : 'Some channels are muted or limited — turn on to be notified everywhere.'}</span>
          <button class="hp-mute-unmute" id="s-notif-all-on" type="button"${allOn ? ' disabled' : ''}>${allOn ? 'All on' : 'Turn all on'}</button>
        </div>
        <div style="font-size:12px;color:var(--fg-muted);margin-top:6px">Covers every channel, category and role in all your servers. Notifications show while the app is open on this device; fully-closed phone push is coming.</div>
      </div>
    </div>
    <div class="settings-card">
      <div class="settings-field">
        <label>Master Toggle</label>
        <div class="settings-field-row">
          <label class="settings-toggle">
            <input type="checkbox" id="s-notifs-master" ${masterOn ? 'checked' : ''}>
            <span class="settings-toggle-slider"></span>
          </label>
          <span style="font-size:13px;color:var(--fg-muted)">New messages, DMs, @mentions and incoming calls</span>
        </div>
      </div>
      <div class="settings-field">
        <label>Sounds</label>
        <div class="settings-field-row">
          <label class="settings-toggle">
            <input type="checkbox" id="s-sound" ${soundEnabled ? 'checked' : ''}>
            <span class="settings-toggle-slider"></span>
          </label>
          <span style="font-size:13px;color:var(--fg-muted)">Play a short ping for DMs/mentions and a ringtone for calls</span>
        </div>
      </div>
      <div class="settings-field">
        <label>Browser Permission</label>
        <div class="settings-field-row" style="gap:10px">
          <span class="hp-perm-pill" data-state="${esc(perm)}">${esc(permLabel)}</span>
          ${showRequestBtn ? `<button class="hp-mute-unmute" id="s-perm-request" type="button">${perm === 'denied' ? 'Retry' : 'Request'}</button>` : ''}
        </div>
        ${perm === 'denied' ? '<div style="font-size:12px;color:var(--fg-muted);margin-top:4px">Your browser has blocked notifications. Open the site settings next to the URL bar to re-enable.</div>' : ''}
      </div>
    </div>
    <h3 class="settings-heading" style="margin-top:20px;font-size:14px">Muted channels</h3>
    <div class="settings-card">
      ${muted.length === 0
        ? '<div style="font-size:13px;color:var(--fg-muted)">No channels muted. Right-click a channel header to mute.</div>'
        : `<div class="hp-mute-list" id="s-mute-list">
            ${muted.map(m => `<div class="hp-mute-row"><span>#${esc(m)}</span><button class="hp-mute-unmute" data-ch="${esc(m)}" type="button">Unmute</button></div>`).join('')}
          </div>`}
    </div>
  `;

  document.getElementById('s-notifs-master')?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    setNotificationsMasterOn(checked);
  });

  // Global "turn all on": master on + every server → All Messages + unmute all
  // channels + reset per-channel levels + request permission in the gesture.
  document.getElementById('s-notif-all-on')?.addEventListener('click', () => {
    if ('Notification' in window && Notification.permission === 'default') {
      requestNotificationPermissionSync(() => { /* stored by the browser */ });
    }
    try {
      localStorage.setItem('hub-people-notifs-master', 'true');
      localStorage.setItem('hub-people-server-notifs', '{}');
      localStorage.setItem('hub-people-muted-channels', '[]');
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('hub-people-notif:')) localStorage.removeItem(k);
      }
    } catch { /* ignore */ }
    showToast('Notifications on for every channel', 'ok');
    renderNotificationsSection(el);
  });

  document.getElementById('s-sound')?.addEventListener('change', (e) => {
    localStorage.setItem('hub-people-sound', String((e.target as HTMLInputElement).checked));
  });

  document.getElementById('s-perm-request')?.addEventListener('click', () => {
    // Sync-first: the prompt must be called inside the gesture stack on
    // Android Chrome — no awaits before this.
    requestNotificationPermissionSync((next) => {
      if (next === 'granted') showToast('Notifications enabled', 'ok');
      else if (next === 'denied') showToast('Permission blocked by your browser', 'warn');
      renderNotificationsSection(el);
    });
  });

  el.querySelectorAll<HTMLButtonElement>('.hp-mute-unmute[data-ch]').forEach(btn => {
    btn.addEventListener('click', () => {
      const ch = btn.dataset['ch'] || '';
      unmuteChannel(ch);
      renderNotificationsSection(el);
    });
  });
}

function renderPrivacySection(el: HTMLElement): void {
  el.innerHTML = `
    <h2 class="settings-heading">Privacy & Safety</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Data</label>
        <p style="font-size:13px;color:var(--fg-muted);margin-bottom:8px">
          Your data stays on your hub. Nothing is sent to external servers.
          Hub People runs on your own infrastructure.
        </p>
      </div>
      <div class="settings-field">
        <label>Sessions</label>
        <button class="btn btn-danger" id="s-logout-all">Log Out All Devices</button>
      </div>
    </div>

    <div class="settings-card" style="margin-top:16px">
      <div class="settings-field">
        <label>Block List</label>
        <p style="font-size:13px;color:var(--fg-muted);margin-bottom:12px">
          Blocked phone numbers and email addresses are silently dropped from inbound SMS and email.
        </p>
        <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
          <select id="bl-kind" style="padding:6px 10px;border-radius:4px;border:1px solid var(--border);background:var(--bg-secondary);color:var(--fg);font-size:13px">
            <option value="phone">Phone</option>
            <option value="email">Email</option>
          </select>
          <input id="bl-value" type="text" placeholder="Number or address" style="flex:1;min-width:180px;padding:6px 10px;border-radius:4px;border:1px solid var(--border);background:var(--bg-secondary);color:var(--fg);font-size:13px" />
          <input id="bl-reason" type="text" placeholder="Reason (optional)" style="flex:1;min-width:120px;padding:6px 10px;border-radius:4px;border:1px solid var(--border);background:var(--bg-secondary);color:var(--fg);font-size:13px" />
          <button class="btn btn-primary" id="bl-add" style="white-space:nowrap">Block</button>
        </div>
        <div id="bl-list" style="font-size:13px;color:var(--fg-muted)">Loading...</div>
      </div>
    </div>

    <div class="settings-card" style="margin-top:16px;border:1px solid var(--danger, #f23f42)">
      <div class="settings-field">
        <label style="color:var(--danger, #f23f42)">Delete Account</label>
        <p style="font-size:13px;color:var(--fg-muted);margin-bottom:8px">
          Permanently remove your Hub Ecosystems account. Your personal server and its contents are wiped.
          Messages you've posted in shared servers are kept for thread integrity but shown as [deleted].
          This cannot be undone.
        </p>
        <button class="btn btn-danger" id="s-delete-account">Delete Account</button>
      </div>
    </div>
  `;

  document.getElementById('s-logout-all')?.addEventListener('click', () => {
    confirmModal('Log out all devices', 'Log out all devices? You will need to log in again.', () => {
      void post('/api/auth/logout/all', {}).then(() => {
        document.cookie = 'hub_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        localStorage.clear();
        window.location.reload();
      }).catch(e => showToast(String(e), 'err'));
    }, { confirmLabel: 'Log Out All', variant: 'danger' });
  });

  document.getElementById('s-delete-account')?.addEventListener('click', () => {
    const me = state.people.find(p => p.id === state.ownerId);
    const displayName = me?.name || '';
    if (!displayName) { showToast('Your display name could not be resolved', 'err'); return; }
    promptModal(
      'Delete account',
      `Type "${displayName}" to confirm permanent account deletion.`,
      (typed) => {
        if (typed.trim() !== displayName.trim()) {
          if (typed.trim() !== '') showToast('Confirmation did not match', 'err');
          return;
        }
        void post('/api/auth/delete-account', {
          person_id: state.ownerId,
          confirm_name: typed,
          reason: 'user-initiated',
        }).then(() => {
          document.cookie = 'hub_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          localStorage.clear();
          showToast('Account deleted', 'ok');
          window.location.href = '/';
        }).catch(e => showToast(String(e), 'err'));
      },
      { placeholder: displayName, confirmLabel: 'Delete account', required: true }
    );
  });

  void loadBlockList();

  document.getElementById('bl-add')?.addEventListener('click', () => {
    const kind = (document.getElementById('bl-kind') as HTMLSelectElement).value;
    const value = (document.getElementById('bl-value') as HTMLInputElement).value.trim();
    const reason = (document.getElementById('bl-reason') as HTMLInputElement).value.trim();
    if (!value) { showToast('Enter a value to block', 'err'); return; }
    void post('/api/blocklist/add', { kind, value, reason }).then(() => {
      showToast('Blocked', 'ok');
      (document.getElementById('bl-value') as HTMLInputElement).value = '';
      (document.getElementById('bl-reason') as HTMLInputElement).value = '';
      void loadBlockList();
    }).catch(e => showToast(String(e), 'err'));
  });
}

interface BlockedEntry {
  id: string;
  kind: string;
  value: string;
  reason: string;
  created_at: string;
}

async function loadBlockList(): Promise<void> {
  const container = document.getElementById('bl-list');
  if (!container) return;
  try {
    const res = await get('/api/blocklist') as { status: string; data: BlockedEntry[] };
    const items = res.data || [];
    if (items.length === 0) {
      container.textContent = 'No blocked contacts.';
      return;
    }
    container.innerHTML = items.map(item => `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border)">
        <span style="background:var(--bg-tertiary);padding:2px 6px;border-radius:3px;font-size:11px;text-transform:uppercase">${esc(item.kind)}</span>
        <span style="flex:1;color:var(--fg)">${esc(item.value)}</span>
        <span style="color:var(--fg-muted);font-size:12px">${esc(item.reason || '')}</span>
        <button class="btn btn-danger" style="padding:2px 8px;font-size:11px" data-bl-remove="${esc(item.id)}">Remove</button>
      </div>
    `).join('');
    container.querySelectorAll<HTMLButtonElement>('[data-bl-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset['blRemove'] || '';
        void post(`/api/blocklist/${id}/remove`, {}).then(() => {
          showToast('Unblocked', 'ok');
          void loadBlockList();
        }).catch(e => showToast(String(e), 'err'));
      });
    });
  } catch (e) {
    container.textContent = 'Failed to load block list.';
  }
}

function renderServerSection(el: HTMLElement): void {
  const server = state.servers.find(s => s.id === state.currentServer);

  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Server Settings</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }

  el.innerHTML = `
    <h2 class="settings-heading">Server Settings — ${esc(server.name)}</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Server Name</label>
        <div class="settings-field-row">
          <input type="text" id="s-server-name" class="settings-input" value="${esc(server.name)}">
          <button class="btn btn-primary" id="s-save-server">Save</button>
        </div>
      </div>
      <div class="settings-field">
        <label>Server Icon</label>
        <div class="settings-field-row" style="gap:12px">
          <div id="s-icon-preview" class="settings-icon-preview">
            ${server.icon && server.icon.startsWith('/') ? `<img src="${esc(server.icon)}" style="width:100%;height:100%;object-fit:cover;border-radius:50%">` : esc(server.icon || server.name.charAt(0).toUpperCase())}
          </div>
          <div style="flex:1">
            <input type="text" id="s-server-icon" class="settings-input" value="${esc(server.icon || '')}" placeholder="Emoji, letter, or upload image" maxlength="200" style="margin-bottom:8px">
            <input type="file" id="s-icon-upload" accept="image/*" style="display:none">
            <button class="btn btn-secondary btn-sm" id="s-icon-upload-btn" style="font-size:12px">Upload Image</button>
          </div>
        </div>
      </div>
      <div class="settings-field">
        <label>Subtitle / Note</label>
        <div class="settings-field-row">
          <textarea id="s-server-subtitle" class="settings-input settings-textarea" placeholder="e.g. HQ, Dev, Staging" maxlength="200" rows="3">${esc(server.subtitle || '')}</textarea>
        </div>
        <div style="font-size:12px;color:var(--fg-muted);margin-top:4px">Shown beneath the server name. Only owners and admins can edit this.</div>
      </div>
      <div class="settings-field">
        <label>Server Type</label>
        <div style="font-size:14px;color:var(--fg-muted)">${esc(server.type || 'personal')}</div>
      </div>
    </div>
    <div class="settings-card" style="margin-top:16px">
      <div class="settings-field">
        <label>Members</label>
        <div id="s-member-list" style="margin-top:8px;max-height:360px;overflow-y:auto">Loading members...</div>
      </div>
    </div>
    <div class="settings-card" style="margin-top:16px">
      <div class="settings-field">
        <label>Danger Zone</label>
        <button class="btn btn-danger" id="s-delete-server">Delete Server</button>
      </div>
    </div>
  `;

  // Load members list
  const members = state.serverMembers.filter(m => m.server_id === server.id);
  const memberListEl = document.getElementById('s-member-list');
  if (memberListEl && members.length > 0) {
    memberListEl.innerHTML = members.map(m => {
      const person = state.people.find(p => p.id === m.person_id);
      const name = person?.name || `Member-${m.person_id.substring(0, 6)}`;
      const initial = name.charAt(0).toUpperCase();
      const isOwner = server.owner_id === m.person_id;
      const roleLabel = isOwner ? '<span style="color:var(--accent);font-size:11px;font-weight:600">OWNER</span>' : (m.role === 'admin' ? '<span style="color:var(--status-idle);font-size:11px;font-weight:600">ADMIN</span>' : '');
      const removeBtn = !isOwner ? `<button class="btn btn-sm btn-danger s-remove-member" data-person="${esc(m.person_id)}" style="margin-left:auto;padding:4px 8px;font-size:11px">Remove</button>` : '';
      return `<div style="display:flex;align-items:center;gap:10px;padding:8px;border-radius:4px;border-bottom:1px solid var(--border)">
        <div style="width:32px;height:32px;border-radius:50%;background:var(--accent);color:var(--fg-on-accent);display:flex;align-items:center;justify-content:center;font-weight:600;font-size:14px;flex-shrink:0">${esc(initial)}</div>
        <div>
          <div style="font-weight:600;font-size:14px">${esc(name)}</div>
          ${roleLabel}
        </div>
        ${removeBtn}
      </div>`;
    }).join('');

    memberListEl.querySelectorAll<HTMLButtonElement>('.s-remove-member').forEach(btn => {
      btn.addEventListener('click', () => {
        const personId = btn.dataset['person'];
        if (!personId) return;
        confirmModal('Remove member', 'Remove this member from the server?', () => {
          void post(`/api/servers/${server.id}/members/remove`, { person_id: personId }).then(() => {
            showToast('Member removed', 'ok');
            btn.closest('div[style]')?.remove();
          }).catch(e => showToast(String(e), 'err'));
        }, { confirmLabel: 'Remove', variant: 'danger' });
      });
    });
  } else if (memberListEl) {
    memberListEl.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">No members</p>';
  }

  // Icon upload
  document.getElementById('s-icon-upload-btn')?.addEventListener('click', () => {
    document.getElementById('s-icon-upload')?.click();
  });
  document.getElementById('s-icon-upload')?.addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('files', file);
    try {
      const res = await fetch(`${API_BASE}/api/uploads`, { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (data.status === 'ok' && data.data?.[0]?.url) {
        const url = data.data[0].url;
        (document.getElementById('s-server-icon') as HTMLInputElement).value = url;
        const preview = document.getElementById('s-icon-preview');
        if (preview) preview.innerHTML = `<img src="${url}" style="width:100%;height:100%;object-fit:cover;border-radius:50%">`;
        showToast('Icon uploaded — click Save', 'ok');
      }
    } catch (err) { errToast(err); }
  });

  document.getElementById('s-save-server')?.addEventListener('click', () => {
    const newName = (document.getElementById('s-server-name') as HTMLInputElement)?.value.trim();
    const newIcon = (document.getElementById('s-server-icon') as HTMLInputElement)?.value.trim();
    const newSubtitle = (document.getElementById('s-server-subtitle') as HTMLTextAreaElement)?.value.trim();
    void post('/api/servers/update', {
      id: server.id,
      name: newName || server.name,
      icon: newIcon,
      subtitle: newSubtitle ?? '',
    }).then(() => {
      showToast('Server updated', 'ok');
      server.name = newName || server.name;
      if (newIcon) server.icon = newIcon;
      server.subtitle = newSubtitle ?? '';
    }).catch(e => showToast(String(e), 'err'));
  });

  document.getElementById('s-delete-server')?.addEventListener('click', () => {
    const isPersonal = server.is_personal === 1 || server.type === 'personal';
    const warning = isPersonal
      ? "Delete your personal server? Your own data here will be permanently removed. Any messages you've posted in other servers (including the shared Hub Ecosystems server) remain visible in those servers, and their owners keep control of that data. This cannot be undone."
      : `Delete "${server.name}"? This cannot be undone.`;
    confirmModal('Delete server', warning, () => {
      const ownerId = state.ownerId || '';
      void post(`/api/servers/${encodeURIComponent(server.id)}/delete`, { owner_id: ownerId }).then(() => {
        showToast('Server deleted', 'ok');
        window.location.reload();
      }).catch(e => showToast(String(e), 'err'));
    }, { confirmLabel: 'Delete', variant: 'danger' });
  });
}

function renderRolesSection(el: HTMLElement): void {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Roles</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }

  const roles = state.roles.filter(r => r.server_id === server.id);

  el.innerHTML = `
    <h2 class="settings-heading">Roles — ${esc(server.name)}</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Create New Role</label>
        <div class="settings-field-row">
          <input type="text" id="s-role-name" class="settings-input" placeholder="Role name" style="flex:2">
          <input type="color" id="s-role-color" value="#16a34a" title="Role color (HTML color picker requires literal hex)" style="width:40px;height:40px;border:none;cursor:pointer;border-radius:4px">
          <button class="btn btn-primary" id="s-create-role">Create</button>
        </div>
      </div>
    </div>
    <div class="settings-card" style="margin-top:16px">
      <div class="settings-field">
        <label>Existing Roles (${roles.length})</label>
        <div id="s-roles-list">
          ${roles.length === 0 ? '<p style="color:var(--fg-muted);font-size:13px">No roles created</p>' : ''}
          ${roles.map(r => `
            <div class="role-item" style="display:flex;align-items:center;gap:10px;padding:10px;border-bottom:1px solid var(--border)">
              <span style="width:16px;height:16px;border-radius:50%;background:${esc(r.color)};flex-shrink:0"></span>
              <span style="font-weight:600;flex:1">${esc(r.name)}</span>
              <span style="font-size:11px;color:var(--fg-muted)">pos ${r.position}</span>
              <button class="btn btn-sm btn-danger s-delete-role" data-role="${esc(r.id)}" style="padding:4px 8px;font-size:11px">Delete</button>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;

  document.getElementById('s-create-role')?.addEventListener('click', () => {
    const nameInput = document.getElementById('s-role-name') as HTMLInputElement;
    const colorInput = document.getElementById('s-role-color') as HTMLInputElement;
    const name = nameInput?.value.trim();
    if (!name) { nameInput?.focus(); return; }

    void post('/api/roles/add', {
      server_id: server.id,
      name,
      color: colorInput?.value || '#16a34a',
    }).then(() => {
      showToast(`Role "${name}" created`, 'ok');
      // Reload roles
      void get<typeof state.roles>('/api/roles').then(r => {
        state.roles = r;
        renderRolesSection(el);
      });
    }).catch(e => showToast(String(e), 'err'));
  });

  el.querySelectorAll<HTMLButtonElement>('.s-delete-role').forEach(btn => {
    btn.addEventListener('click', () => {
      const roleId = btn.dataset['role'];
      if (!roleId) return;
      confirmModal('Delete role', 'Delete this role?', () => {
        void post('/api/roles/delete', { id: roleId }).then(() => {
          showToast('Role deleted', 'ok');
          void get<typeof state.roles>('/api/roles').then(r => {
            state.roles = r;
            renderRolesSection(el);
          });
        }).catch(e => showToast(String(e), 'err'));
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });
}

interface ChannelPermRow {
  channel_id: string;
  role_id: string;
  can_read: number;
  can_write: number;
  can_download: number;
}

function renderChannelsSection(el: HTMLElement): void {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Channels &amp; Permissions</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }
  const textChannels = state.channels.filter(c =>
    c.server_id === server.id &&
    (c.channel_type === 'text' || c.channel_type === 'announcement')
  );
  const roles = state.roles.filter(r => r.server_id === server.id);

  el.innerHTML = `
    <h2 class="settings-heading">Channels &amp; Permissions — ${esc(server.name)}</h2>
    <p style="color:var(--fg-muted);font-size:13px;margin-bottom:12px">
      A channel with no rows below is open to everyone. Adding even one row switches that channel to explicit-allow — only roles you grant can read or write.
      Server owners always bypass these checks.
    </p>
    <div class="settings-card">
      ${textChannels.length === 0
        ? '<p style="color:var(--fg-muted);padding:8px">No text channels in this server.</p>'
        : '<div id="s-channel-list"></div>'}
    </div>
  `;

  const listEl = document.getElementById('s-channel-list');
  if (!listEl) return;

  for (const ch of textChannels) {
    const row = document.createElement('div');
    row.className = 'channel-perm-row';
    row.style.cssText = 'border-bottom:1px solid var(--border);padding:10px 0';
    row.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;cursor:pointer" class="cp-head" data-channel="${esc(ch.id)}">
        <span style="color:var(--fg-muted);font-weight:700">#</span>
        <span style="font-weight:600;flex:1">${esc(ch.name)}</span>
        <span class="cp-chevron" style="color:var(--fg-muted)">&#9656;</span>
      </div>
      <div class="cp-body" id="cp-body-${esc(ch.id)}" style="display:none;margin-top:10px"></div>
    `;
    listEl.appendChild(row);

    const head = row.querySelector('.cp-head') as HTMLElement;
    const body = row.querySelector('.cp-body') as HTMLElement;
    const chev = row.querySelector('.cp-chevron') as HTMLElement;

    head.addEventListener('click', async () => {
      const open = body.style.display !== 'none';
      if (open) {
        body.style.display = 'none';
        chev.textContent = '\u25B8';
        return;
      }
      body.style.display = 'block';
      chev.textContent = '\u25BE';
      body.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">Loading...</p>';
      try {
        const rows = await get<ChannelPermRow[]>(`/api/channel-permissions?channel_id=${encodeURIComponent(ch.id)}`);
        renderChannelPermBody(body, ch.id, roles, rows);
      } catch (e) {
        body.innerHTML = `<p style="color:var(--err)">Failed to load: ${esc(String(e))}</p>`;
      }
    });
  }
}

function renderChannelPermBody(
  body: HTMLElement,
  channelId: string,
  roles: typeof state.roles,
  rows: ChannelPermRow[],
): void {
  if (roles.length === 0) {
    body.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">No roles defined. Create roles first, then return here.</p>';
    return;
  }
  const byRole = new Map(rows.map(r => [r.role_id, r]));
  body.innerHTML = `
    <table style="width:100%;font-size:13px;border-collapse:collapse">
      <thead>
        <tr style="text-align:left;color:var(--fg-muted)">
          <th style="padding:6px 4px">Role</th>
          <th style="padding:6px 4px;width:70px">Read</th>
          <th style="padding:6px 4px;width:70px">Write</th>
          <th style="padding:6px 4px;width:70px">Download</th>
          <th style="padding:6px 4px;width:60px"></th>
        </tr>
      </thead>
      <tbody id="cp-rows-${esc(channelId)}"></tbody>
    </table>
  `;
  const tbody = body.querySelector(`#cp-rows-${CSS.escape(channelId)}`) as HTMLElement;
  for (const role of roles) {
    const row = byRole.get(role.id);
    const canRead = row ? row.can_read === 1 : false;
    const canWrite = row ? row.can_write === 1 : false;
    const canDownload = row ? row.can_download === 1 : true;
    const tr = document.createElement('tr');
    tr.style.cssText = 'border-top:1px solid var(--border)';
    tr.innerHTML = `
      <td style="padding:8px 4px">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${esc(role.color || 'var(--status-offline)')};margin-right:6px"></span>
        ${esc(role.name)}
      </td>
      <td style="padding:8px 4px"><input type="checkbox" data-perm="read" data-role="${esc(role.id)}" ${canRead ? 'checked' : ''}></td>
      <td style="padding:8px 4px"><input type="checkbox" data-perm="write" data-role="${esc(role.id)}" ${canWrite ? 'checked' : ''}></td>
      <td style="padding:8px 4px"><input type="checkbox" data-perm="download" data-role="${esc(role.id)}" ${canDownload ? 'checked' : ''}></td>
      <td style="padding:8px 4px">
        ${row ? `<button class="btn btn-sm btn-danger cp-clear" data-role="${esc(role.id)}" style="padding:2px 6px;font-size:11px">Clear</button>` : ''}
      </td>
    `;
    tbody.appendChild(tr);
  }

  const save = async (roleId: string) => {
    const readEl = body.querySelector<HTMLInputElement>(`input[data-perm="read"][data-role="${CSS.escape(roleId)}"]`);
    const writeEl = body.querySelector<HTMLInputElement>(`input[data-perm="write"][data-role="${CSS.escape(roleId)}"]`);
    const dlEl = body.querySelector<HTMLInputElement>(`input[data-perm="download"][data-role="${CSS.escape(roleId)}"]`);
    if (!readEl || !writeEl) return;
    try {
      await post('/api/channel-permissions', {
        channel_id: channelId,
        role_id: roleId,
        can_read: readEl.checked,
        can_write: writeEl.checked,
        can_download: dlEl?.checked ?? true,
      });
      showToast('Saved', 'ok');
    } catch (e) {
      showToast(String(e), 'err');
    }
  };
  body.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
    cb.addEventListener('change', () => {
      const roleId = cb.dataset['role'];
      if (roleId) void save(roleId);
    });
  });
  body.querySelectorAll<HTMLButtonElement>('.cp-clear').forEach(btn => {
    btn.addEventListener('click', () => {
      const roleId = btn.dataset['role'];
      if (!roleId) return;
      void post('/api/channel-permissions/delete', {
        channel_id: channelId,
        role_id: roleId,
      }).then(async () => {
        showToast('Cleared', 'ok');
        const rowsNow = await get<ChannelPermRow[]>(`/api/channel-permissions?channel_id=${encodeURIComponent(channelId)}`);
        renderChannelPermBody(body, channelId, roles, rowsNow);
      }).catch(e => showToast(String(e), 'err'));
    });
  });
}

interface SupportLinkRow {
  token: string;
  server_id: string;
  created_by: string | null;
  kind: 'text' | 'voice' | 'video';
  ttl: 'temp' | 'perm';
  label: string;
  channel_id: string | null;
  used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  status: 'unused' | 'used' | 'expired' | 'revoked';
  url: string;
}

interface CreateLinkResp {
  token: string;
  url: string;
  kind: string;
  ttl: string;
  label: string;
  expires_at: string | null;
  created_at: string;
}

// Server Settings → Management. Owner / admin only. Single toggle that
// flips `servers.management_enabled`. When ON, the channel-list shows the
// contractor / agreements / approvals / directory rows for this server.
function renderManagementSection(el: HTMLElement): void {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Management</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }

  const enabled = (server.management_enabled ?? 0) === 1;
  const ownerId = state.ownerId || '';
  const myMember = state.serverMembers.find(m => m.server_id === server.id && m.person_id === ownerId);
  const isOwner = server.owner_id === ownerId;
  const isAdmin = myMember?.role === 'admin' || myMember?.role === 'owner';
  const canToggle = isOwner || isAdmin;

  el.innerHTML = `
    <h2 class="settings-heading">Management — ${esc(server.name)}</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Enable management apps for this server</label>
        <p style="color:var(--fg-muted);font-size:13px;margin:4px 0 12px">
          When on, this server's channel list shows the contractor, agreements,
          approvals, and directory entries. Best for business servers; leave
          off for personal, family, community, or gaming servers.
        </p>
        <label style="display:inline-flex;align-items:center;gap:10px;cursor:${canToggle ? 'pointer' : 'not-allowed'};opacity:${canToggle ? '1' : '0.6'}">
          <input type="checkbox" id="s-management-toggle"${enabled ? ' checked' : ''}${canToggle ? '' : ' disabled'} style="width:18px;height:18px;cursor:inherit">
          <span style="font-weight:600">${enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
        ${canToggle ? '' : '<p style="color:var(--fg-muted);font-size:12px;margin-top:8px">Only the server owner or an admin can change this.</p>'}
      </div>
    </div>
  `;

  const toggle = document.getElementById('s-management-toggle') as HTMLInputElement | null;
  if (!toggle || !canToggle) return;

  toggle.addEventListener('change', () => {
    const next = toggle.checked;
    toggle.disabled = true;
    void post(`/api/servers/${encodeURIComponent(server.id)}/management-toggle`, {
      enabled: next,
      person_id: ownerId,
    })
      .then(() => {
        server.management_enabled = next ? 1 : 0;
        showToast(next ? 'Management enabled' : 'Management disabled', 'ok');
        window.dispatchEvent(new CustomEvent('hub-servers-changed'));
        renderManagementSection(el);
      })
      .catch(e => {
        toggle.checked = !next;
        toggle.disabled = false;
        showToast(String(e), 'err');
      });
  });
}

async function renderSupportSection(el: HTMLElement): Promise<void> {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Adhoc Support</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }

  el.innerHTML = `
    <h2 class="settings-heading">Adhoc Support — ${esc(server.name)}</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Generate a Support Link</label>
        <p style="color:var(--fg-muted);font-size:13px;margin:4px 0 12px">
          Calendly-shaped URLs that drop the clicker into a private text/voice/video
          room. Walk-up guests are not added to the server.
        </p>
        <div class="settings-field-row" style="gap:8px;flex-wrap:wrap">
          <select id="sl-kind" class="settings-input" style="flex:0 0 auto">
            <option value="text">Text</option>
            <option value="voice">Voice</option>
            <option value="video">Video</option>
          </select>
          <select id="sl-ttl" class="settings-input" style="flex:0 0 auto">
            <option value="temp">Temporary (24h, 1 use)</option>
            <option value="perm">Permanent</option>
          </select>
          <input type="text" id="sl-label" class="settings-input" placeholder="Label (optional)" maxlength="200" style="flex:1;min-width:160px">
          <button class="btn btn-primary" id="sl-create">Generate</button>
        </div>
      </div>
    </div>
    <div class="settings-card" style="margin-top:16px">
      <div class="settings-field">
        <label>Active links</label>
        <div id="sl-list" style="margin-top:8px">Loading…</div>
      </div>
    </div>
  `;

  document.getElementById('sl-create')?.addEventListener('click', () => {
    const kind = (document.getElementById('sl-kind') as HTMLSelectElement | null)?.value || 'text';
    const ttl = (document.getElementById('sl-ttl') as HTMLSelectElement | null)?.value || 'temp';
    const label = (document.getElementById('sl-label') as HTMLInputElement | null)?.value.trim() || '';
    void post<CreateLinkResp>(`/api/server/${encodeURIComponent(server.id)}/support-link`, {
      kind, ttl, label, created_by: state.ownerId || '',
    }).then((resp) => {
      showToast('Support link created', 'ok');
      void navigator.clipboard?.writeText(resp.url).catch(() => {});
      const labelEl = document.getElementById('sl-label') as HTMLInputElement | null;
      if (labelEl) labelEl.value = '';
      void renderSupportSection(el);
    }).catch(e => showToast(String(e), 'err'));
  });

  // Populate the active-links list.
  const listEl = document.getElementById('sl-list');
  if (!listEl) return;
  try {
    const rows = await get<SupportLinkRow[]>(
      `/api/server/${encodeURIComponent(server.id)}/support-links?person_id=${encodeURIComponent(state.ownerId || '')}`
    );
    if (rows.length === 0) {
      listEl.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">No links yet.</p>';
      return;
    }
    listEl.innerHTML = rows.map(r => `
      <div class="sl-row" style="display:flex;align-items:center;gap:10px;padding:10px;border-bottom:1px solid var(--border)">
        <span style="font-size:11px;text-transform:uppercase;color:var(--fg-muted);min-width:48px">${esc(r.kind)}</span>
        <span style="font-size:11px;color:var(--fg-muted);min-width:36px">${esc(r.ttl)}</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.label ? esc(r.label) : '<span style="color:var(--fg-muted)">(no label)</span>'}</span>
        <span class="sl-status sl-status-${esc(r.status)}" style="font-size:11px;text-transform:uppercase;color:${r.status === 'unused' || r.status === 'used' ? 'var(--accent)' : 'var(--fg-muted)'}">${esc(r.status)}</span>
        <button class="btn btn-sm sl-copy" data-url="${esc(r.url)}" style="padding:4px 8px;font-size:11px">Copy</button>
        ${r.revoked_at ? '' : `<button class="btn btn-sm btn-danger sl-revoke" data-token="${esc(r.token)}" style="padding:4px 8px;font-size:11px">Revoke</button>`}
      </div>
    `).join('');

    listEl.querySelectorAll<HTMLButtonElement>('.sl-copy').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = btn.dataset['url'] || '';
        void navigator.clipboard?.writeText(url).then(() => {
          showToast('Link copied', 'ok');
        }).catch(() => showToast(url, 'ok'));
      });
    });

    listEl.querySelectorAll<HTMLButtonElement>('.sl-revoke').forEach(btn => {
      btn.addEventListener('click', () => {
        const token = btn.dataset['token'] || '';
        confirmModal(
          'Revoke this support link?',
          'Anyone holding the link will get a 404. The channel itself is left intact for review.',
          () => {
            void post(`/api/server/${encodeURIComponent(server.id)}/support-link/${encodeURIComponent(token)}/revoke`,
              { person_id: state.ownerId || '' }
            ).then(() => {
              showToast('Link revoked', 'ok');
              void renderSupportSection(el);
            }).catch(e => showToast(String(e), 'err'));
          }
        );
      });
    });
  } catch (e) {
    listEl.innerHTML = `<p style="color:var(--danger);font-size:13px">${esc(String(e))}</p>`;
  }
}

function renderAuditSection(el: HTMLElement): void {
  el.innerHTML = `
    <h2 class="settings-heading">Audit Log</h2>
    <div class="settings-card" id="audit-log-list">
      <p style="color:var(--fg-muted)">Loading audit log...</p>
    </div>
  `;

  // Load activity logs
  void get<Array<{handler: string; payload_summary: string; ts: string; ip: string}>>('/api/logs?limit=50')
    .then(logs => {
      const listEl = document.getElementById('audit-log-list');
      if (!listEl) return;
      if (logs.length === 0) {
        listEl.innerHTML = '<p style="color:var(--fg-muted);padding:12px">No activity recorded</p>';
        return;
      }
      listEl.innerHTML = `
        <table style="width:100%;font-size:13px;border-collapse:collapse">
          <thead>
            <tr style="text-align:left;border-bottom:1px solid var(--border)">
              <th style="padding:8px;font-weight:600;color:var(--fg-muted)">Action</th>
              <th style="padding:8px;font-weight:600;color:var(--fg-muted)">Details</th>
              <th style="padding:8px;font-weight:600;color:var(--fg-muted)">Time</th>
            </tr>
          </thead>
          <tbody>
            ${logs.map(l => `
              <tr style="border-bottom:1px solid var(--border)">
                <td style="padding:8px;font-weight:500">${esc(l.handler)}</td>
                <td style="padding:8px;color:var(--fg-muted)">${esc(l.payload_summary || '')}</td>
                <td style="padding:8px;color:var(--fg-muted);white-space:nowrap">${new Date(l.ts).toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    })
    .catch(() => {
      const listEl = document.getElementById('audit-log-list');
      if (listEl) listEl.innerHTML = '<p style="color:var(--fg-muted)">Failed to load audit log</p>';
    });
}

// ── Orchestrator-powered dev worker management ──

interface OrchestratorWorker {
  container_name: string;
  state: string;
  reserved_role: string | null;
  last_used_at: string | null;
  created_at: string;
  idle_seconds: number;
  last_session_id: string | null;
  spawn_reason: string | null;
}

function formatIdleTime(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function stateChipColor(st: string): string {
  switch (st) {
    case 'idle': return 'green';
    case 'busy': return 'amber';
    case 'unhealthy': return 'red';
    default: return 'blue';
  }
}

function workerCard(w: OrchestratorWorker): string {
  const isAnchor = w.reserved_role === 'anchor';
  const idleHours = w.idle_seconds / 3600;
  const reapingSoon = !isAnchor && w.state === 'idle' && idleHours > 20;
  const chipColor = isAnchor ? 'blue' : stateChipColor(w.state);
  const displayState = isAnchor ? 'anchor' : w.state;

  return `
    <div class="settings-card" style="padding:12px 16px;position:relative${reapingSoon ? ';border-color:var(--warning)' : ''}">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
        <span class="ds-dot ds-dot-${chipColor}" aria-hidden="true"></span>
        <strong style="font-size:14px">${esc(w.container_name)}</strong>
        <span style="font-size:11px;padding:2px 8px;border-radius:var(--radius);background:var(--surface-2);color:var(--text-secondary)">${esc(displayState)}</span>
        ${isAnchor ? '<span style="font-size:10px;padding:2px 6px;border-radius:var(--radius);background:var(--info);color:#fff;font-weight:600">ANCHOR</span>' : ''}
        ${reapingSoon ? '<span style="font-size:10px;padding:2px 6px;border-radius:var(--radius);background:var(--warning);color:#fff;font-weight:600">REAPING SOON</span>' : ''}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 16px;font-size:12px;color:var(--text-secondary)">
        <div>Created: ${relativeTime(w.created_at)}</div>
        <div>Last used: ${relativeTime(w.last_used_at)}</div>
        <div>Idle: ${formatIdleTime(w.idle_seconds)}</div>
        <div>Spawned: ${esc(w.spawn_reason ?? '—')}</div>
      </div>
      <div style="display:flex;gap:6px;margin-top:10px;justify-content:flex-end">
        ${!isAnchor ? `<button class="btn btn-sm dw-kill" data-name="${esc(w.container_name)}" style="padding:4px 12px;font-size:11px;background:var(--danger);color:#fff;border:none;border-radius:var(--radius);cursor:pointer">Kill</button>` : `<button class="btn btn-sm dw-kill" data-name="${esc(w.container_name)}" data-anchor="1" style="padding:4px 12px;font-size:11px;background:var(--surface-2);color:var(--text-secondary);border:1px solid var(--border);border-radius:var(--radius);cursor:pointer" title="Force-kill anchor">Kill (force)</button>`}
      </div>
    </div>
  `;
}

async function renderDevWorkersSection(el: HTMLElement): Promise<void> {
  el.innerHTML = `
    <h2 class="settings-heading">Dev Workers</h2>
    <p style="color:var(--text-secondary);font-size:13px;margin-bottom:12px">
      Auto-managed dev containers. Workers auto-spin when the queue has work and no idle worker exists.
      Idle workers (>24h) are auto-reaped. Anchor workers (dev1, dev2) are never auto-reaped.
    </p>
    <div style="display:flex;gap:8px;margin-bottom:16px">
      <button class="btn btn-secondary" id="dw-refresh" style="padding:6px 14px;font-size:13px">Refresh</button>
      <button class="btn btn-primary" id="dw-spawn" style="padding:6px 14px;font-size:13px">Spawn new</button>
    </div>
    <div id="dw-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:12px">
      <p style="color:var(--text-secondary)">Loading workers...</p>
    </div>
  `;

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const paint = async (): Promise<void> => {
    const grid = document.getElementById('dw-grid');
    if (!grid) { if (pollTimer) clearInterval(pollTimer); return; }
    try {
      const workers = await get<OrchestratorWorker[]>('/api/orchestrator/workers');
      if (workers.length === 0) {
        grid.innerHTML = '<p style="color:var(--text-secondary)">No workers found. Spawn one or wait for the orchestrator to discover running containers.</p>';
        return;
      }
      grid.innerHTML = workers.map(w => workerCard(w)).join('');

      // Bind kill buttons
      grid.querySelectorAll<HTMLButtonElement>('.dw-kill').forEach(btn => {
        btn.addEventListener('click', () => {
          const name = btn.dataset['name'];
          if (!name) return;
          const isAnchor = btn.dataset['anchor'] === '1';
          confirmModal(
            `Kill ${name}?`,
            isAnchor
              ? `${name} is an anchor worker. This will force-kill it. Are you sure?`
              : `This will stop and remove ${name}. The container will be destroyed.`,
            async () => {
              btn.disabled = true;
              btn.textContent = 'Killing...';
              try {
                await post('/api/orchestrator/reap', {
                  container_name: name,
                  force: isAnchor,
                });
                showToast(`${name} reaped`, 'ok');
                await paint();
              } catch (e) {
                showToast(String(e), 'err');
                btn.disabled = false;
                btn.textContent = isAnchor ? 'Kill (force)' : 'Kill';
              }
            },
          );
        });
      });
    } catch (e) {
      grid.innerHTML = `<p style="color:var(--danger)">Failed to load workers: ${esc(String(e))}</p>`;
    }
  };

  document.getElementById('dw-refresh')?.addEventListener('click', () => void paint());

  document.getElementById('dw-spawn')?.addEventListener('click', async () => {
    const btn = document.getElementById('dw-spawn') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Spawning...'; }
    try {
      const result = await post<{ container_name: string; state: string }>('/api/orchestrator/spawn', { reason: 'manual' });
      showToast(`Spawned ${result.container_name}`, 'ok');
      await paint();
    } catch (e) {
      showToast(String(e), 'err');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Spawn new'; }
    }
  });

  await paint();

  // Poll every 15s
  pollTimer = setInterval(() => {
    if (!document.getElementById('dw-grid')) {
      if (pollTimer) clearInterval(pollTimer);
      return;
    }
    void paint();
  }, 15_000);
}

async function renderContactsSection(el: HTMLElement): Promise<void> {
  el.innerHTML = `
    <h2 class="settings-heading">Contacts</h2>
    <div class="settings-card">
      <div class="settings-field">
        <label>Import from Google Contacts</label>
        <p style="font-size:13px;color:var(--fg-muted);margin-bottom:12px">
          Export your contacts from Google Contacts as CSV, then upload the file here.
          Existing contacts are matched by email or phone and updated rather than duplicated.
        </p>
        <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
          <label style="display:inline-flex;align-items:center;gap:8px;padding:8px 16px;border-radius:4px;border:1px dashed var(--border);cursor:pointer;font-size:13px;color:var(--fg-muted)">
            <input type="file" accept=".csv" id="ci-file" style="display:none" />
            Choose CSV file
          </label>
          <span id="ci-filename" style="font-size:13px;color:var(--fg-muted)">No file selected</span>
        </div>
        <button class="btn btn-primary" id="ci-import" style="margin-top:12px" disabled>Import Contacts</button>
      </div>
    </div>
    <div id="ci-result" style="margin-top:12px"></div>
  `;

  const fileInput = document.getElementById('ci-file') as HTMLInputElement;
  const importBtn = document.getElementById('ci-import') as HTMLButtonElement;
  const filenameSpan = document.getElementById('ci-filename') as HTMLSpanElement;

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) {
      filenameSpan.textContent = file.name;
      importBtn.disabled = false;
    } else {
      filenameSpan.textContent = 'No file selected';
      importBtn.disabled = true;
    }
  });

  importBtn.addEventListener('click', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    importBtn.disabled = true;
    importBtn.textContent = 'Importing...';
    const reader = new FileReader();
    reader.onload = () => {
      const csv = reader.result as string;
      void post('/api/contacts/import', { csv, source: 'google_csv' }).then((res: unknown) => {
        const data = (res as { data: { total: number; created: number; updated: number; skipped: number } }).data;
        const resultEl = document.getElementById('ci-result');
        if (resultEl) {
          resultEl.innerHTML = `
            <div class="settings-card" style="border:1px solid var(--success, #16a34a)">
              <div class="settings-field">
                <label style="color:var(--success, #16a34a)">Import Complete</label>
                <p style="font-size:13px;color:var(--fg-muted)">
                  Total: ${data.total} | Created: ${data.created} | Updated: ${data.updated} | Skipped: ${data.skipped}
                </p>
              </div>
            </div>
          `;
        }
        showToast(`Imported ${data.created} new, updated ${data.updated}`, 'ok');
        importBtn.textContent = 'Import Contacts';
        importBtn.disabled = false;
        fileInput.value = '';
        filenameSpan.textContent = 'No file selected';
      }).catch(e => {
        showToast(String(e), 'err');
        importBtn.textContent = 'Import Contacts';
        importBtn.disabled = false;
      });
    };
    reader.readAsText(file);
  });
}

// ── Storage section ────────────────────────────────────────────────

interface StorageUsage {
  person_id: string;
  used_bytes: number;
  limit_bytes: number;
  file_count: number;
  pct: number;
}

interface DiskInfo {
  total_bytes: number;
  used_bytes: number;
  free_bytes: number;
  pct_used: number;
}

interface MountInfo {
  label: string;
  path: string;
  status: string;
}

interface UserStorageRow {
  person_id: string;
  name: string;
  used_bytes: number;
  file_count: number;
  pct_of_limit: number;
}

interface StorageOverview {
  disk: DiskInfo;
  mounts: MountInfo[];
  per_user: UserStorageRow[];
  total_uploads_bytes: number;
  total_uploads_count: number;
  limit_per_user_bytes: number;
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function storagePctColor(pct: number): string {
  if (pct >= 90) return 'var(--status-dnd, #ef4444)';
  if (pct >= 75) return 'var(--status-idle, #f59e0b)';
  return 'var(--accent)';
}

async function renderStorageSection(el: HTMLElement): Promise<void> {
  const server = state.servers.find(s => s.id === state.currentServer);
  if (!server) {
    el.innerHTML = '<h2 class="settings-heading">Storage</h2><p style="color:var(--fg-muted);padding:20px">Select a server first</p>';
    return;
  }

  const ownerId = state.ownerId || '';
  const isOwner = server.owner_id === ownerId;

  // Every user sees their own usage
  el.innerHTML = '<h2 class="settings-heading">Storage</h2><div style="padding:20px;color:var(--fg-muted)">Loading...</div>';

  try {
    const usage = await get<StorageUsage>(`/api/storage/usage?person_id=${encodeURIComponent(ownerId)}`);
    const pctColor = storagePctColor(usage.pct);
    const warnBanner = !isOwner && usage.pct >= 80
      ? `<div style="background:var(--bg-secondary);border:1px solid ${usage.pct >= 95 ? 'var(--status-dnd, #ef4444)' : 'var(--status-idle, #f59e0b)'};border-radius:6px;padding:10px 14px;margin-bottom:16px;font-size:13px;color:var(--fg-primary)">
          ${usage.pct >= 95
            ? 'You have almost reached your storage limit. Delete old attachments to free up space.'
            : 'You are approaching your storage limit. Consider removing unused files.'}
        </div>`
      : '';

    let html = `
      <h2 class="settings-heading">Storage</h2>
      ${warnBanner}
      <div class="settings-card">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
          <label style="font-weight:600">Your Storage</label>
          <span style="font-size:13px;color:var(--fg-muted)">${fmtBytes(usage.used_bytes)} / ${fmtBytes(usage.limit_bytes)} (${usage.pct.toFixed(1)}%)</span>
        </div>
        <div style="background:var(--bg-tertiary);border-radius:4px;height:8px;overflow:hidden">
          <div style="width:${Math.min(usage.pct, 100)}%;height:100%;background:${pctColor};border-radius:4px;transition:width 0.3s"></div>
        </div>
        <div style="font-size:12px;color:var(--fg-muted);margin-top:8px">${usage.file_count} file${usage.file_count !== 1 ? 's' : ''} uploaded</div>
      </div>
    `;

    // Owner/admin gets the full VPS overview
    if (isOwner) {
      try {
        const overview = await get<StorageOverview>('/api/storage/overview');

        // VPS disk
        const diskPct = overview.disk.pct_used;
        const diskColor = storagePctColor(diskPct);

        html += `
          <div class="settings-card" style="margin-top:16px">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>
              <label style="font-weight:600;font-size:15px">VPS Disk</label>
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
              <span style="font-size:13px;color:var(--fg-muted)">Used: ${fmtBytes(overview.disk.used_bytes)}</span>
              <span style="font-size:13px;color:var(--fg-muted)">Free: ${fmtBytes(overview.disk.free_bytes)}</span>
              <span style="font-size:13px;font-weight:600">${fmtBytes(overview.disk.total_bytes)} total</span>
            </div>
            <div style="background:var(--bg-tertiary);border-radius:4px;height:10px;overflow:hidden">
              <div style="width:${Math.min(diskPct, 100)}%;height:100%;background:${diskColor};border-radius:4px;transition:width 0.3s"></div>
            </div>
            <div style="font-size:12px;color:var(--fg-muted);margin-top:6px">${diskPct.toFixed(1)}% used</div>
          </div>
        `;

        // Mounts
        html += `
          <div class="settings-card" style="margin-top:16px">
            <label style="font-weight:600;display:block;margin-bottom:12px">Mounts</label>
            ${overview.mounts.map(m => `
              <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border)">
                <div>
                  <div style="font-weight:500;font-size:13px">${esc(m.label)}</div>
                  <div style="font-size:11px;color:var(--fg-muted);font-family:var(--font-mono, monospace)">${esc(m.path)}</div>
                </div>
                <span style="font-size:12px;font-weight:600;padding:2px 8px;border-radius:10px;background:${m.status === 'ok' ? 'var(--success-bg, rgba(22,163,74,0.15))' : 'var(--danger-bg, rgba(239,68,68,0.15))'};color:${m.status === 'ok' ? 'var(--success, #16a34a)' : 'var(--status-dnd, #ef4444)'}">${esc(m.status)}</span>
              </div>
            `).join('')}
          </div>
        `;

        // Per-user breakdown
        html += `
          <div class="settings-card" style="margin-top:16px">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
              <label style="font-weight:600">Per-User Breakdown</label>
              <span style="font-size:12px;color:var(--fg-muted)">${overview.total_uploads_count} files | ${fmtBytes(overview.total_uploads_bytes)} total</span>
            </div>
            <div style="font-size:12px;color:var(--fg-muted);margin-bottom:12px">Limit per user: ${fmtBytes(overview.limit_per_user_bytes)}</div>
        `;

        if (overview.per_user.length === 0) {
          html += '<div style="padding:12px 0;color:var(--fg-muted);font-size:13px">No uploads yet</div>';
        } else {
          for (const u of overview.per_user) {
            const uPctColor = storagePctColor(u.pct_of_limit);
            html += `
              <div style="padding:10px 0;border-bottom:1px solid var(--border)">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
                  <span style="font-weight:500;font-size:13px">${esc(u.name)}</span>
                  <span style="font-size:12px;color:var(--fg-muted)">${fmtBytes(u.used_bytes)} (${u.file_count} file${u.file_count !== 1 ? 's' : ''})</span>
                </div>
                <div style="background:var(--bg-tertiary);border-radius:3px;height:6px;overflow:hidden">
                  <div style="width:${Math.min(u.pct_of_limit, 100)}%;height:100%;background:${uPctColor};border-radius:3px"></div>
                </div>
                <div style="font-size:11px;color:var(--fg-muted);margin-top:4px">${u.pct_of_limit.toFixed(1)}% of limit</div>
              </div>
            `;
          }
        }

        html += '</div>';

        // "Your hub, your data" footer
        html += `
          <div style="margin-top:20px;padding:14px 16px;background:var(--bg-secondary);border-radius:8px;border:1px solid var(--border)">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span style="font-weight:600;font-size:13px;color:var(--accent)">Your hub, your data</span>
            </div>
            <p style="font-size:12px;color:var(--fg-muted);margin:0;line-height:1.5">
              All files are stored on your own infrastructure. Nothing leaves your server
              unless you explicitly share it. Storage limits protect shared resources for everyone.
            </p>
          </div>
        `;
      } catch (e) {
        html += `<div class="settings-card" style="margin-top:16px"><p style="color:var(--status-dnd, #ef4444)">Failed to load admin overview: ${esc(String(e))}</p></div>`;
      }
    } else {
      // Non-owner: "your hub, your data" reassurance
      html += `
        <div style="margin-top:16px;padding:14px 16px;background:var(--bg-secondary);border-radius:8px;border:1px solid var(--border)">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span style="font-weight:600;font-size:13px;color:var(--accent)">Your hub, your data</span>
          </div>
          <p style="font-size:12px;color:var(--fg-muted);margin:0;line-height:1.5">
            Your files are stored on this hub's own infrastructure. You have a
            ${fmtBytes(usage.limit_bytes)} storage allowance. Contact the hub owner if you need more space.
          </p>
        </div>
      `;
    }

    el.innerHTML = html;
  } catch (e) {
    el.innerHTML = `<h2 class="settings-heading">Storage</h2><div class="settings-card"><p style="color:var(--status-dnd, #ef4444)">Failed to load storage info: ${esc(String(e))}</p></div>`;
  }
}

function renderAboutSection(el: HTMLElement): void {
  el.innerHTML = `
    <h2 class="settings-heading">About Hub People</h2>
    <div class="settings-card">
      <p style="margin-bottom:8px"><strong>Hub People</strong> v0.1.0</p>
      <p style="font-size:13px;color:var(--fg-muted);margin-bottom:12px">
        Part of The Hub Ecosystems platform. Self-hosted, privacy-first
        team management and communication.
      </p>
      <p style="font-size:12px;color:var(--fg-muted)">
        Rust + axum backend | TypeScript + Vite frontend | SQLite
      </p>
    </div>
  `;
}
